import React from 'react';
import get from 'lodash/get';
import {
  Action,
  withInject,
} from '@piwikpro/platform';
import {
  Stack,
  Tooltip,
  Icon,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface MaxSessionEventCountProps {
  appId: string
  trackerSettings: TrackerSettings
  appTrackerSettings: TrackerSettingsResponse
  globalTrackerSettings: TrackerSettingsResponse
  t: (key: string, variables?: any) => string
}

const defaultValue = 43200;
const minValue = 1;
const maxValue = 43200;

function FieldError(fieldName: string, message: string) {
  const error: any = new Error(message);

  error.errors = {
    [fieldName]: message,
  };

  return error;
}

class MaxSessionTime extends React.Component<
MaxSessionEventCountProps, {
  useGlobals: boolean
}
> {
  static readonly attributeName = 'session_max_duration_seconds';

  static readonly fieldName = `${this.attributeName}_string`;

  static readonly globalFieldName = `${this.attributeName}_global`;

  static readonly minuteDivisor = 60;

  static readonly hourDivisor = 60 * this.minuteDivisor;

  constructor(props: MaxSessionEventCountProps) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings.attributes?.[
        MaxSessionTime.attributeName
      ] === undefined,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  getTimeUnits() {
    const { t } = this.props;

    return {
      hourUnit: t('apps.details.settings.session-limits.max-session-time.unit.hour'),
      minuteUnit: t('apps.details.settings.session-limits.max-session-time.unit.minute'),
      secondUnit: t('apps.details.settings.session-limits.max-session-time.unit.second'),
    };
  }

  getGlobalValue() {
    const { globalTrackerSettings } = this.props;

    return get(globalTrackerSettings.attributes, MaxSessionTime.attributeName, defaultValue);
  }

  getValue() {
    const { appTrackerSettings } = this.props;
    const { useGlobals } = this.state;

    const globalValue = this.getGlobalValue();
    const applicationValue = get(
      appTrackerSettings.attributes,
      MaxSessionTime.attributeName,
      globalValue,
    );

    return useGlobals ? globalValue : applicationValue;
  }

  getFormattedValue(newValue: number | null = null) {
    const value = newValue === null ? this.getValue() : newValue;

    const hours = Math.floor(value / MaxSessionTime.hourDivisor);
    const minutes = Math.floor((value % MaxSessionTime.hourDivisor) / MaxSessionTime.minuteDivisor);
    const seconds = (
      value - hours * MaxSessionTime.hourDivisor - minutes * MaxSessionTime.minuteDivisor
    );

    const units = this.getTimeUnits();

    return [
      [hours, units.hourUnit],
      [minutes, units.minuteUnit],
      [seconds, units.secondUnit],
    ].filter(([val]) => val !== 0).map(([val, unit]) => `${val}${unit}`).join(' ');
  }

  getParsedValue(values) {
    const { t } = this.props;
    const value = get(values, MaxSessionTime.fieldName);
    const regex = /^(\d+)([\w.]+)$/;

    if (value) {
      const unitArray = value.split(' ').map((entry: string) => [entry, regex.exec(entry)]);

      let hours = 0;
      let minutes = 0;
      let seconds = 0;

      const units = this.getTimeUnits();

      unitArray.forEach(([entry, match]: [entry: any, match: any]) => {
        if (entry) {
          if (match) {
            const [, unitValue, unitSymbol] = match;
            const numberValue = Number(unitValue);

            if (!Number.isNaN(numberValue)) {
              switch (unitSymbol) {
                case units.hourUnit:
                  hours += Number(numberValue);
                  break;
                case units.minuteUnit:
                  minutes += numberValue;
                  break;
                case units.secondUnit:
                  seconds += numberValue;
                  break;
                default:
                  throw FieldError(
                    MaxSessionTime.fieldName,
                    t('apps.details.settings.session-limits.max-session-time.error.unit', {
                      invalidToken: unitSymbol,
                      rawInput: value,
                    }),
                  );
              }
            }
          } else {
            throw FieldError(
              MaxSessionTime.fieldName,
              t('apps.details.settings.session-limits.max-session-time.error.symbol', {
                invalidToken: entry,
                rawInput: value,
              }),
            );
          }
        }
      });

      const result = (
        hours * MaxSessionTime.hourDivisor + minutes * MaxSessionTime.minuteDivisor + seconds
      );

      if (result > maxValue) {
        throw FieldError(
          MaxSessionTime.fieldName,
          t('apps.details.settings.session-limits.max-session-time.error.maximum', {
            maxValue: this.getFormattedValue(maxValue),
          }),
        );
      }

      if (result < minValue) {
        throw FieldError(
          MaxSessionTime.fieldName,
          t('apps.details.settings.session-limits.max-session-time.error.minimum', {
            minValue: this.getFormattedValue(minValue),
          }),
        );
      }

      return result;
    }

    return NaN;
  }

  async handleSubmit(values, {
    setErrors,
    setSubmitting,
    setFieldValue,
  }: {
    setErrors: Function
    setSubmitting: Function
    setFieldValue: Function
  }) {
    const {
      trackerSettings,
      appId,
    } = this.props;

    try {
      const value = this.getParsedValue(values);
      setFieldValue(MaxSessionTime.attributeName, value);
      setFieldValue(
        MaxSessionTime.fieldName,
        this.getFormattedValue(value),
        false,
      );

      await trackerSettings.updateSettingsForApp(appId, {
        [MaxSessionTime.attributeName]: value,
      });
    } catch (err: any) {
      setErrors(err.errors);
      setFieldValue(
        MaxSessionTime.fieldName,
        this.getFormattedValue(),
        false,
      );
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(
    values,
    { setFieldValue, setSubmitting }: {
      setSubmitting: Function
      setFieldValue: Function
    },
  ) {
    const {
      trackerSettings,
      appId,
    } = this.props;

    this.setState({
      useGlobals: values[MaxSessionTime.globalFieldName],
    });

    try {
      if (values[MaxSessionTime.globalFieldName]) {
        await trackerSettings.deleteSettingsForApp(
          appId,
          MaxSessionTime.attributeName,
        );
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [MaxSessionTime.attributeName]: this.getGlobalValue(),
        });
      }
    } catch (err) {
      setFieldValue(MaxSessionTime.globalFieldName, !values[MaxSessionTime.globalFieldName], false);
      this.setState({
        useGlobals: !values[MaxSessionTime.globalFieldName],
      });
    }

    setSubmitting(false);
  }

  render() {
    const { t } = this.props;
    const { useGlobals } = this.state;

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack vertical spacing="narrow" horizontalAlignment="fill">
            <Stack.Item>
              <Action.AutoSavedNumberControl
                id="app-settings-max-session-time"
                label={t('apps.details.settings.session-limits.max-session-time.title')}
                info={t('apps.details.settings.session-limits.max-session-time.description')}
                name={MaxSessionTime.fieldName}
                initialValue={this.getFormattedValue()}
                disabled={useGlobals}
                onSubmit={this.handleSubmit}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-max-session-time-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name={MaxSessionTime.globalFieldName}
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(
  withTranslation(['administration'])(MaxSessionTime as any),
);
